import type { ApexOptions } from 'apexcharts'
import {
  resolveDisplay,
  type ChartCard,
} from '@/features/dashboard/api/schemas'
import type { Theme } from '@/shared/theme/ThemeContext'
import {
  AXIS_FONT_SIZE,
  displayFont,
  FONT,
  sliceColor,
  valueLabelStyle,
  type ChartPalette,
} from './chartTheme'
import type { DeepDiveModel } from './deepDiveModel'
import {
  paddedRange,
  plotWidth,
  signedValue,
  tick,
  wrapLabel,
} from './deepDiveAxis'
import { formatChartValue, seriesTooltipHtml } from './tooltipHtml'

/** A category named as the net of the others ('Net change') is drawn apart. */
export const isNetCategory = (label: string) => /\bnet\b/i.test(label)

/** Past this many px a character of a name is about 6.5 wide. */
const CHAR_PX = 6.5
/** Slices from this share of the whole carry their value. */
const LABELLED_SHARE = 0.08

/**
 * The delta column's colour: the net in the text colour (black), else teal for
 * good news and rose for bad, as the Figma bridges draw them. A rise is
 * good, except where lower is better (a cost).
 */
export const deltaColor = (
  label: string,
  value: number,
  palette: ChartPalette,
  lowerIsBetter = false
) =>
  isNetCategory(label)
    ? palette.text
    : value < 0 !== lowerIsBetter
      ? (palette.categorical[2] ?? palette.negative)
      : (palette.categorical[0] ?? palette.positive)

const baseChart = (
  type: 'bar' | 'donut',
  palette: ChartPalette,
  height: number
): NonNullable<ApexOptions['chart']> => ({
  background: 'transparent',
  toolbar: { show: false },
  fontFamily: FONT,
  foreColor: palette.label,
  type,
  height,
  zoom: { enabled: false },
  animations: { speed: 300 },
})

const valueFormatter =
  (card: ChartCard) =>
  (v: number | null, decimals = 1) =>
    v === null ? '' : formatChartValue(v, card.unit, card.format, decimals)

/**
 * Delta columns: a column per category (a lifecycle stage, a component), green
 * above zero and red below, the net drawn in the neutral text colour.
 */
export const waterfallOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  theme: Theme,
  height: number,
  width = 0
): ApexOptions => {
  const { labels, totals } = model
  const lowerIsBetter = resolveDisplay(card).lowerIsBetter
  const colors = totals.map((v, i) =>
    deltaColor(labels[i] ?? '', v, palette, lowerIsBetter)
  )
  // Names sit side by side on as many lines as they need (cut short past 3).
  const perColumn = plotWidth(width) / Math.max(labels.length, 1)
  const chars = Math.max(Math.floor((perColumn - 8) / CHAR_PX), 6)
  const { min, max, ticks } = paddedRange(totals)
  return {
    chart: baseChart('bar', palette, height),
    colors,
    theme: { mode: theme },
    fill: { opacity: 1 },
    states: { hover: { filter: { type: 'none' } } },
    grid: {
      borderColor: palette.barGrid,
      strokeDashArray: 6,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 12, bottom: 4, right: 12, left: 8 },
    },
    legend: { show: false },
    dataLabels: {
      enabled: true,
      offsetY: -16,
      ...valueLabelStyle(palette),
      formatter: (v: string | number | number[]) =>
        typeof v === 'number' ? signedValue(v, card.unit, card.format) : '',
    },
    plotOptions: {
      bar: {
        distributed: true,
        borderRadius: 3,
        borderRadiusApplication: 'end',
        columnWidth: '56%',
        dataLabels: { position: 'top' },
      },
    },
    // A hairline in the column's colour keeps a column of almost nothing (the
    // net of a quiet month) in sight.
    stroke: { show: true, width: 2, colors },
    annotations: {
      yaxis: [{ y: 0, borderColor: palette.baseline, strokeDashArray: 0 }],
    },
    xaxis: {
      categories: labels.map(label => wrapLabel(label, chars, 4)),
      labels: {
        show: true,
        hideOverlappingLabels: false,
        rotate: 0,
        trim: false,
        style: { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      min,
      max,
      tickAmount: ticks,
      opposite: true,
      labels: {
        style: { colors: palette.textAlt, fontSize: AXIS_FONT_SIZE },
        formatter: (v: number) => tick(v, card.unit, card.format),
      },
    },
    tooltip: {
      theme,
      intersect: true,
      shared: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) =>
        seriesTooltipHtml({
          label: labels[dataPointIndex] ?? '',
          unit: card.unit,
          format: card.format,
          palette,
          rows: [
            {
              value: totals[dataPointIndex] ?? 0,
              color: colors[dataPointIndex] ?? palette.goal,
            },
          ],
        }),
    },
  }
}

/** A donut: a slice per category, the total at its centre. No legend: a slice is named in its tooltip. */
export const donutOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  theme: Theme,
  height: number
): ApexOptions => {
  const { labels } = model
  const value = valueFormatter(card)
  const slices = model.totals.map(v => Math.max(v, 0))
  const sum = slices.reduce((a, b) => a + b, 0)
  const colors = slices.map((_, i) => sliceColor(palette, i))
  const caption = model.metrics[0]?.name ?? 'Total'
  return {
    chart: baseChart('donut', palette, height),
    colors,
    labels,
    theme: { mode: theme },
    legend: { show: false },
    // The big slices carry their value; the small ones would only crowd (the tooltip has it).
    dataLabels: {
      enabled: true,
      ...valueLabelStyle(palette, palette.card),
      formatter: (_: unknown, opts?: { seriesIndex?: number }) => {
        const share = slices[opts?.seriesIndex ?? -1] ?? 0
        return sum > 0 && share / sum >= LABELLED_SHARE ? value(share) : ''
      },
    },
    stroke: { width: 2, colors: [palette.card] },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            name: { show: false },
            value: {
              show: true,
              color: palette.text,
              fontFamily: displayFont,
              fontSize: '40px',
              fontWeight: 700,
              offsetY: -4,
              formatter: (v: string) => value(Number(v)),
            },
            total: {
              show: true,
              showAlways: true,
              label: caption,
              color: palette.textAlt,
              fontFamily: FONT,
              fontSize: '13px',
              formatter: () => value(sum),
            },
          },
        },
      },
    },
    tooltip: {
      theme,
      custom: ({ seriesIndex }: { seriesIndex: number }) =>
        seriesTooltipHtml({
          label: caption,
          unit: card.unit,
          format: card.format,
          palette,
          rows: [
            {
              label: labels[seriesIndex] ?? '',
              value: slices[seriesIndex] ?? 0,
              color: colors[seriesIndex] ?? palette.goal,
              suffix: `${sum > 0 ? Math.round(((slices[seriesIndex] ?? 0) / sum) * 100) : 0}%`,
            },
          ],
        }),
    },
  }
}
